import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../components/layout';
import { useChatContext } from '../../contexts/ChatContext'; // Make sure to import ChatContext
import { Application, User } from '../../types/knowledge-base.ts';
import { API_ENDPOINTS } from '../../config/api.config';
import { apiService } from '../../services/apiService';
import ApplicationList from '../../components/rag/RAGList';
import ContentSkeleton from "@/components/loading/ContentSkeleton.tsx";
import '../../../scss/ragApplications.scss';

interface ApplicationResponse {
    applications: Application[];
    total: number;
    page: number;
    totalPages: number;
    limit: number;
}

const RAGApplicationsPage: React.FC = () => {
    const navigate = useNavigate();
    const { user: authUser } = useAuth();
    const [user] = useState<User | null>(authUser);
    const [applications, setApplications] = useState<Application[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef<HTMLInputElement>(null);
    const modalState = useModal();

    // Mobile responsiveness
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const { openChat } = useChatContext(); // Use the ChatContext

    // Listen for window resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleToggleStatus = async (id: string, isEnabled: boolean): Promise<void> => {
        try {
            modalState.setIsLoading(true);

            // Make API call first
            await apiService.patch(`${API_ENDPOINTS.RAG.TOGGLE_ENABLED}/${id}`, {
                enabled: !isEnabled
            });

            // Update state only after successful API call
            setApplications(prevApps =>
                prevApps.map(app =>
                    app.id === id ? { ...app, enabled: !isEnabled } : app
                )
            );

            modalState.setSuccess(`Application ${isEnabled ? 'disabled' : 'enabled'} successfully`);
        } catch (error) {
            console.error('Error toggling application status:', error);
            modalState.setError(error instanceof Error ? error.message : 'Failed to update application status');
        } finally {
            modalState.setIsLoading(false);
        }
    };

    // This function is now only called when the search is actually performed
    // (after debounce in the child component)
    const handleSearch = (query: string) => {
        setSearchQuery(query);
        setCurrentPage(1);
        fetchApplications(1, query);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchApplications(page, searchQuery);
    };

    const fetchApplications = async (page: number, search?: string): Promise<void> => {
        try {
            setIsLoading(true);
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: isMobile ? '5' : '10', // Fetch fewer items for mobile
                ...(search ? { search } : {})
            });

            const response = await apiService.get<ApplicationResponse>(
                `${API_ENDPOINTS.RAG.LIST}?${queryParams}`
            );

            setApplications(response.applications);
            setTotalPages(response.totalPages);
            setCurrentPage(response.page);
            setTotal(response.total);
        } catch (error) {
            console.error('Failed to fetch applications:', error);
            modalState.setError('Failed to load applications');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchApplications(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile]); // Refetch when screen size changes

    if (!user) return null;

    return (
        <div className="page-container">
            <div className="mb-4">
                <h5 className="mb-3">RAG Applications</h5>
                {isLoading ? (
                    <ContentSkeleton />
                ) : (
                    <>
                        <div className="search-container">
                            <ApplicationList
                                applications={applications}
                                onManage={(id) => navigate(`/rag-applications/${id}/edit`)}
                                onDeploy={(id) => handleToggleStatus(id, applications.find(app => app.id === id)?.enabled || false)}
                                onCreate={() => navigate('/rag-applications/create')}
                                isLoading={isLoading}
                                userRole={user?.role}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                total={total}
                                onPageChange={handlePageChange}
                                searchQuery={searchQuery}
                                onSearch={handleSearch}
                                searchInputRef={searchInputRef}
                                className="application-item"
                                // Still pass these props, but they'll be used differently now
                                onChat={openChat} // Use the ChatContext's openChat
                                isMobile={isMobile}
                            />
                        </div>
                    </>
                )}
            </div>

            {/* Remove all chat rendering - this is now handled by the ChatContext */}
        </div>
    );
};

export default RAGApplicationsPage;